import React, { useMemo, useState } from "react";

import { pluralize } from "@common/helpers/string";
import { FilterPickerListActions } from "@components/pageHeader/filters/FilterPickerList/FilterPickerListParts";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import {
    type IdPickerValueProps,
    pickSingleId,
    toIdArray,
} from "@design-system/formControl/idPickerValue";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useCounterpartyDropdownOptions } from "./useCounterpartyDropdownOptions";

interface CounterpartyDropdownOwnProps extends Pick<
    ValueControlProps<string | null, HTMLButtonElement>,
    "onBlur" | "invalid" | "disabled" | "ref"
> {
    buttonSize?: TriggerButtonProps["size"];
    container?: HTMLElement;
    /** Id of a visible element elsewhere that names the trigger. */
    "aria-labelledby"?: string;
}

/**
 * Counterparty picker: one counterparty id (or `null`) by default, an id
 * array with `multiple`. Rows are checkboxes in both modes, so a single
 * pick is unticked (to `null`) by choosing it again.
 */
export type CounterpartyDropdownProps = CounterpartyDropdownOwnProps &
    IdPickerValueProps<string>;

const toSelectionLabel = ({
    selectedIds,
    namesById,
}: {
    selectedIds: readonly string[];
    namesById: ReadonlyMap<string, string>;
}) => {
    if (selectedIds.length === 0) {
        return undefined;
    }
    const singleName =
        selectedIds.length === 1 ? namesById.get(selectedIds[0]) : undefined;
    return (
        singleName ??
        `${selectedIds.length} ${pluralize("counterparty", selectedIds.length)}`
    );
};

export const CounterpartyDropdown: React.FC<CounterpartyDropdownProps> = ({
    value,
    onValueChange,
    multiple,
    onBlur,
    invalid,
    disabled,
    ref,
    buttonSize,
    container,
    "aria-labelledby": ariaLabelledby,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const counterpartyIds = useMemo(() => toIdArray(value), [value]);
    const { items, namesById, status, loadMore, onInputValueChange } =
        useCounterpartyDropdownOptions({
            isOpen,
            selectedIds: counterpartyIds,
        });

    const handleValueChange = (nextIds: string[]) => {
        if (multiple) {
            onValueChange(nextIds);
            return;
        }
        const counterpartyId = pickSingleId({
            previousIds: counterpartyIds,
            nextIds,
        });
        onValueChange(counterpartyId);
        if (counterpartyId !== null) {
            setIsOpen(false);
        }
    };

    return (
        <Picker.Select<string, true>
            multiple
            items={items}
            value={counterpartyIds}
            onValueChange={handleValueChange}
            open={isOpen}
            onOpenChange={setIsOpen}
            onBlur={onBlur}
            aria-invalid={invalid}
            disabled={disabled}
            container={container}
            ref={ref}
            searchable
            filterable={false}
            onInputValueChange={onInputValueChange}
            status={status}
            {...loadMore}
            searchPlaceholder="Search counterparties..."
            emptyLabel="No matching counterparty"
            placeholder="Select counterparty"
            renderValue={(selectedIds) =>
                toSelectionLabel({ selectedIds, namesById })
            }
            renderFooter={() =>
                multiple && counterpartyIds.length > 0 ? (
                    <FilterPickerListActions
                        selectedCount={counterpartyIds.length}
                        onDeselect={() => onValueChange([])}
                    />
                ) : null
            }
            trigger={
                <Picker.Button
                    variant="primary"
                    size={buttonSize ?? "md"}
                    {...ariaLabelledbyProps(ariaLabelledby)}
                />
            }
        />
    );
};
